import { useEffect, useState, useCallback, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import { IconButton } from '../../components/ui/Button.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import Pagination from '../../components/ui/Pagination.jsx'
import { Input, Select } from '../../components/ui/Field.jsx'
import api from '../../lib/api.js'
import { AdminTable, Td } from './AdminTable.jsx'

/*
 * Admin — global audit log (GET /audit). Read-only, server-side paginated, with
 * action + resource-type + search filters (applied server-side over the whole
 * dataset). Inventory §3 (audit tab).
 */
const ACTION_TONE = { LOGIN: 'info', LOGOUT: 'neutral', CREATE: 'success', UPDATE: 'warning', DELETE: 'danger' }
const toneFor = (a = '') => ACTION_TONE[a.split('_')[0]] || (/(DELETE|REJECT|FAIL)/.test(a) ? 'danger' : /(CREATE|APPROVE|SUCCESS)/.test(a) ? 'success' : /UPDATE/.test(a) ? 'warning' : 'neutral')

export default function AuditTab({ endpoint = '/audit' }) {
  const { t } = useTranslation()
  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [page, setPage] = useState(1)
  const [action, setAction] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    const params = { page, per_page: 25 }
    if (action) params.action = action
    if (from) params.start_date = `${from}T00:00:00` // whole-day inclusive range
    if (to) params.end_date = `${to}T23:59:59`
    api.get(endpoint, { params })
      .then(({ data }) => {
        setRows(data?.logs || [])
        const pg = data?.pagination || {}
        setPagination({ page: pg.page || 1, pages: pg.pages || Math.max(1, Math.ceil((pg.total || 0) / 25)), total: pg.total ?? (data?.logs || []).length })
      }).finally(() => setLoading(false))
  }, [page, action, from, to, endpoint])
  useEffect(() => { load() }, [load])

  const columns = [
    { key: 'when', label: t('admin.audit.when') }, { key: 'action', label: t('admin.audit.action') },
    { key: 'resource', label: t('admin.audit.resource') }, { key: 'user', label: t('admin.users.email') },
    { key: 'company', label: t('admin.tabs.companies') }, { key: 'ip', label: t('admin.audit.ip') },
    { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Labelled label={t('admin.audit.action')}>
          <Select value={action} onChange={(e) => { setAction(e.target.value); setPage(1) }} style={{ width: 'auto', height: 42 }}>
            <option value="">{t('admin.audit.allActions')}</option>
            {['LOGIN', 'CREATE', 'UPDATE', 'DELETE'].map((a) => <option key={a} value={a}>{a}</option>)}
          </Select>
        </Labelled>
        <Labelled label={t('admin.audit.from')}>
          <Input type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPage(1) }} style={{ height: 42 }} />
        </Labelled>
        <Labelled label={t('admin.audit.to')}>
          <Input type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPage(1) }} style={{ height: 42 }} />
        </Labelled>
        {(from || to || action) && (
          <button onClick={() => { setFrom(''); setTo(''); setAction(''); setPage(1) }} style={{ height: 42, background: 'none', border: 'none', color: 'var(--color-primary)', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>{t('admin.audit.clear')}</button>
        )}
      </div>
      <AdminTable columns={columns} rows={rows} loading={loading} empty={t('admin.audit.empty')}
        renderRow={(l) => (
          <>
            <Td><span style={{ fontSize: 13, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{fmtTs(l.timestamp)}</span></Td>
            <Td><Badge tone={toneFor(l.action)}>{l.action}</Badge></Td>
            <Td>{l.resource_type ? <span style={{ fontSize: 13 }}>{l.resource_type}{l.resource_id ? ` #${l.resource_id}` : ''}</span> : '—'}</Td>
            <Td>{l.user_email || '—'}</Td>
            <Td>{l.company_name || '—'}</Td>
            <Td><span style={{ fontFamily: 'monospace', fontSize: 12.5, color: 'var(--color-text-muted)' }}>{l.ip_address || '—'}</span></Td>
            <Td align="right"><IconButton icon="eye" title={t('admin.audit.viewDetails')} onClick={() => setDetail(l)} /></Td>
          </>
        )} />
      {pagination.pages > 1 && (
        <Pagination page={pagination.page} totalPages={pagination.pages} total={pagination.total} unit={t('admin.audit.unit')}
          onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }) }} />
      )}
      {detail && <DetailModal t={t} log={detail} onClose={() => setDetail(null)} />}
    </>
  )
}

function Labelled({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
      {children}
    </label>
  )
}

function DetailModal({ t, log, onClose }) {
  const meta = [
    [t('admin.audit.action'), log.action], [t('admin.audit.when'), fmtTs(log.timestamp)],
    [t('admin.audit.resource'), log.resource_type ? `${log.resource_type}${log.resource_id ? ` #${log.resource_id}` : ''}` : '—'],
    [t('admin.users.email'), log.user_email || '—'], [t('admin.tabs.companies'), log.company_name || '—'],
    [t('admin.audit.ip'), log.ip_address || '—'], ['User-Agent', log.user_agent || '—'],
  ]
  return (
    <Modal open onClose={onClose} maxWidth={620}>
      <div style={{ padding: 24, maxHeight: '85vh', overflowY: 'auto' }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 16 }}>{t('admin.audit.detailTitle')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', marginBottom: 18 }}>
          {meta.map(([k, v]) => (
            <Fragment key={k}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text-muted)' }}>{k}</span>
              <span style={{ fontSize: 13.5, color: 'var(--color-ink)', wordBreak: 'break-word' }}>{v}</span>
            </Fragment>
          ))}
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>{t('admin.audit.detailsJson')}</div>
        <pre style={{ margin: 0, padding: 14, background: 'var(--color-ink)', color: '#e6e1f2', borderRadius: 10, fontSize: 12.5, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(log.details ?? {}, null, 2)}</pre>
      </div>
    </Modal>
  )
}

function fmtTs(ts) {
  if (!ts) return '—'
  const d = new Date(ts)
  return `${d.toLocaleDateString('fr-BE')} ${d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })}`
}
